import {computePosition, autoUpdate, flip, offset, shift, limitShift, arrow} from '@floating-ui/dom';
import {Component, Element, h, Prop, Host} from '@stencil/core';
import {getShadowDom, onClickOutside} from '@/utils';
import {TooltipVariants} from '@/types';
import {applyTooltipArrowPositionStyle} from '@/utils';

/**
 * @slot tooltip-trigger - The element you need to interact with, to display the tooltip
 * @slot tooltip-content - The content that will be cloned and injected in the actual tooltip. This slot content is hidden.
 */ /*  */
@Component({
    tag: 'joy-tooltip-trigger',
    styleUrl: 'tooltip-trigger.scss',
    shadow: true,
})
export class TooltipTrigger {
    @Element() host!: HTMLJoyTooltipTriggerElement;
    private tooltipElement: HTMLJoyTooltipElement | null = null;
    private destroyFn!: () => void;

    /** Use basic selector to inject the tooltip in a specific DOM location. Default is body */
    @Prop({reflect: true}) appendTo: 'body' | 'host' = 'body';
    /** Define the event needed to display the tooltip */
    @Prop({reflect: true}) event: 'mouseenter' | 'click' = 'mouseenter';
    /** Color theme. 2 possible values */
    @Prop({reflect: true}) variant: TooltipVariants = 'primary';
    /** Tooltip position. @floating-ui Placement value*/
    @Prop({reflect: true}) position:
        | 'top'
        | 'right'
        | 'bottom'
        | 'left'
        | 'bottom-start'
        | 'bottom-end'
        | 'top-start'
        | 'top-end'
        | 'left-start'
        | 'left-end'
        | 'right-start'
        | 'right-end' = 'bottom-start';
    /** Allows to override tooltip size. Allows to more easily calculate its position */
    @Prop({reflect: true}) tooltipWidth = 250;

    private hasContentSlot() {
        return !!this.host.querySelector('[slot="tooltip-content"]');
    }

    private get rootElement() {
        const body = document.body;

        if (this.appendTo === 'body') {
            return body;
        }

        return getShadowDom(this.host, body);
    }

    private async setTooltipPosition(tooltip: HTMLJoyTooltipElement) {
        const arrowElement = await tooltip.getArrow();

        autoUpdate(this.host, tooltip, () => {
            computePosition(this.host, tooltip, {
                placement: this.position,
                middleware: [
                    offset(10),
                    shift({
                        limiter: limitShift(),
                    }),
                    flip({
                        fallbackPlacements: ['bottom', 'top', 'left', 'right'],
                    }),
                    arrow({
                        element: arrowElement,
                    }),
                ],
            }).then(({x, y, placement, middlewareData}) => {
                Object.assign(tooltip.style, {
                    left: `${x}px`,
                    top: `${y}px`,
                });

                applyTooltipArrowPositionStyle(arrowElement, placement, middlewareData.arrow, '-4px');
            });
        });
    }

    private slotContent(slotName: string): HTMLElement[] {
        let slotNodes: HTMLElement[] = [];

        Array.from(getShadowDom(this.host).querySelectorAll('slot'))
            .filter((slot) => slot.name === slotName)
            .map((slot) => {
                slotNodes = slot.assignedNodes() as HTMLElement[];
            });
        return [...slotNodes];
    }

    private createTooltip() {
        if (!this.tooltipElement && this.hasContentSlot()) {
            const tooltip = document.createElement('joy-tooltip');

            this.slotContent('tooltip-content').map((node) => {
                const clone = node.cloneNode(true);
                tooltip.appendChild(clone);
            });

            /**
             * Tooltip final style
             */
            tooltip.setAttribute('variant', this.variant);
            tooltip.style.maxWidth = `${this.tooltipWidth}px`;

            this.setTooltipPosition(tooltip);
            this.rootElement.appendChild(tooltip);

            this.tooltipElement = tooltip;

            this.onClickOutsideListener();
        }
    }

    private onMouseEnter = (e: Event) => {
        if (this.event !== 'mouseenter') {
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        this.createTooltip();
    };

    private onMouseLeave = () => {
        if (this.event === 'mouseenter') {
            this.destroyTooltip();
        }
    };

    private onClick = () => {
        if (this.event !== 'click') {
            return;
        }
        this.createTooltip();
    };

    private onClickOutsideListener() {
        if (this.event === 'click') {
            onClickOutside(this.host, this.destroyFn);
        }
    }

    private destroyTooltip() {
        if (this.tooltipElement) {
            this.tooltipElement.classList.add('joy-tooltip--hiding');

            setTimeout(() => {
                this.tooltipElement?.remove();
                this.tooltipElement = null;
            }, 200);
        }
    }

    connectedCallback() {
        this.destroyFn = this.destroyTooltip.bind(this);
    }

    disconnectedCallback() {
        this.destroyTooltip();
    }

    render() {
        return (
            <Host onClick={this.onClick} onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
                <slot name="tooltip-trigger" />
                <div hidden class="joy-tooltip__content">
                    <slot name="tooltip-content" />
                </div>
            </Host>
        );
    }
}
